import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AddressAutocomplete } from '../components/maps/AddressAutocomplete';
import {
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  AlertTriangle,
  Phone,
  MapPin,
  Calendar,
  Loader2,
  Globe,
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  FolderOpen,
  FileText,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { PatientCustomFieldGroupsTab, PatientCustomFieldsTab, PatientMoreInfoPanel, PatientFormPanel } from '../components/patients';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Common country calling codes for Latin America and international
const countryCodes = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
];

const PatientForm = ({ patient, countries, cities, onSubmit, onClose, t }) => {
  const [formData, setFormData] = useState(patient || {
    first_name: '',
    last_name: '',
    document_type: 'CC',
    document_number: '',
    birth_date: '',
    gender: 'male',
    phone_country_code: '+57',
    phone: '',
    mobile: '',
    email: '',
    whatsapp_username: '',
    address: {
      street: '',
      city: '',
      city_id: '',
      department: '',
      country: '',
      country_id: ''
    },
    blood_type: '',
    notes: '',
    custom_field_values: []
  });
  const [loading, setLoading] = useState(false);
  const [isMoreInfoOpen, setIsMoreInfoOpen] = useState(false);

  // Initialize custom_field_values from patient if editing
  useEffect(() => {
    if (patient && patient.custom_field_values) {
      setFormData(prev => ({
        ...prev,
        custom_field_values: patient.custom_field_values || []
      }));
    }
  }, [patient]);

  // Filter cities by selected country using useMemo to avoid infinite loops
  const filteredCities = useMemo(() => {
    if (formData.address?.country_id) {
      return cities.filter(c => c.country_id === formData.address.country_id && c.status === 'active');
    }
    return cities.filter(c => c.status === 'active');
  }, [formData.address?.country_id, cities]);

  const handleCountryChange = (countryId) => {
    const country = countries.find(c => c.id === countryId);
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        country_id: countryId,
        country: country?.name || '',
        city_id: '',
        city: '',
        department: ''
      }
    });
  };

  const handleCityChange = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        city_id: cityId,
        city: city?.name || '',
        department: city?.state_department || ''
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving patient:', error);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">{t('form.firstName')} *</Label>
          <Input
            id="first_name"
            value={formData.first_name}
            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
            required
            data-testid="patient-first-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">{t('form.lastName')} *</Label>
          <Input
            id="last_name"
            value={formData.last_name}
            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
            required
            data-testid="patient-last-name"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="document_type">{t('form.documentType')} *</Label>
          <Select
            value={formData.document_type}
            onValueChange={(value) => setFormData({...formData, document_type: value})}
          >
            <SelectTrigger data-testid="patient-doc-type">
              <SelectValue placeholder={t('common:select')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CC">{t('documentTypes.CC')}</SelectItem>
              <SelectItem value="TI">{t('documentTypes.TI')}</SelectItem>
              <SelectItem value="CE">{t('documentTypes.CE')}</SelectItem>
              <SelectItem value="PA">{t('documentTypes.PA')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="document_number">{t('form.documentNumber')} *</Label>
          <Input
            id="document_number"
            value={formData.document_number}
            onChange={(e) => setFormData({...formData, document_number: e.target.value})}
            required
            data-testid="patient-doc-number"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="birth_date">{t('form.birthDate')} *</Label>
          <Input
            id="birth_date"
            type="date"
            value={formData.birth_date}
            onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
            required
            data-testid="patient-birth-date"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="gender">{t('form.gender')} *</Label>
          <Select
            value={formData.gender}
            onValueChange={(value) => setFormData({...formData, gender: value})}
          >
            <SelectTrigger data-testid="patient-gender">
              <SelectValue placeholder={t('common:select')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">{t('genders.male')}</SelectItem>
              <SelectItem value="female">{t('genders.female')}</SelectItem>
              <SelectItem value="other">{t('genders.other')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">{t('form.phone')} *</Label>
          <div className="flex gap-2">
            <Select
              value={formData.phone_country_code || '+57'}
              onValueChange={(value) => setFormData({...formData, phone_country_code: value})}
            >
              <SelectTrigger className="w-[120px]" data-testid="patient-phone-country">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="3001234567"
              required
              className="flex-1"
              data-testid="patient-phone"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t('form.email')}</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            data-testid="patient-email"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="whatsapp_username">{t('form.whatsapp')}</Label>
        <Input
          id="whatsapp_username"
          value={formData.whatsapp_username || ''}
          onChange={(e) => setFormData({...formData, whatsapp_username: e.target.value})}
          placeholder={t('form.whatsappPlaceholder')}
          data-testid="patient-whatsapp"
        />
        <p className="text-xs text-muted-foreground">{t('form.whatsappHint')}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="street">{t('form.address')} *</Label>
        <AddressAutocomplete
          value={formData.address?.street || ''}
          onChange={(value) => setFormData({...formData, address: {...formData.address, street: value}})}
          onSelect={(place) => {
            // Auto-fill address components from Google Places
            setFormData({
              ...formData,
              address: {
                ...formData.address,
                street: place.formatted_address || place.address,
                department: place.department || formData.address?.department,
                latitude: place.latitude,
                longitude: place.longitude
              }
            });
          }}
          placeholder={t('form.addressPlaceholder')}
          data-testid="patient-street"
        />
        {formData.address?.latitude && (
          <p className="text-xs text-green-600 dark:text-green-400">
            {t('form.coordinates')}: {formData.address.latitude?.toFixed(6)}, {formData.address.longitude?.toFixed(6)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('form.country')} *</Label>
          <Select
            value={formData.address?.country_id || ''}
            onValueChange={handleCountryChange}
          >
            <SelectTrigger data-testid="patient-country">
              <SelectValue placeholder={t('form.selectCountry')} />
            </SelectTrigger>
            <SelectContent>
              {countries.filter(c => c.status === 'active').map((country) => (
                <SelectItem key={country.id} value={country.id}>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    {country.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('form.city')} *</Label>
          <Select
            value={formData.address?.city_id || ''}
            onValueChange={handleCityChange}
            disabled={!formData.address?.country_id}
          >
            <SelectTrigger data-testid="patient-city">
              <SelectValue placeholder={formData.address?.country_id ? t('form.selectCity') : t('form.selectCountryFirst')} />
            </SelectTrigger>
            <SelectContent>
              {filteredCities.filter(c => c.status === 'active').map((city) => (
                <SelectItem key={city.id} value={city.id}>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    {city.name} {city.state_department && `(${city.state_department})`}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="department">{t('form.department')}</Label>
          <Input
            id="department"
            value={formData.address?.department || ''}
            disabled
            className="bg-muted"
            placeholder={t('form.departmentHint')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="blood_type">{t('form.bloodType')}</Label>
          <Select
            value={formData.blood_type || ''}
            onValueChange={(value) => setFormData({...formData, blood_type: value})}
          >
            <SelectTrigger data-testid="patient-blood-type">
              <SelectValue placeholder={t('common:select')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="A+">A+</SelectItem>
              <SelectItem value="A-">A-</SelectItem>
              <SelectItem value="B+">B+</SelectItem>
              <SelectItem value="B-">B-</SelectItem>
              <SelectItem value="AB+">AB+</SelectItem>
              <SelectItem value="AB-">AB-</SelectItem>
              <SelectItem value="O+">O+</SelectItem>
              <SelectItem value="O-">O-</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">{t('form.notes')}</Label>
        <Textarea
          id="notes"
          value={formData.notes || ''}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder={t('form.notesPlaceholder')}
          data-testid="patient-notes"
        />
      </div>

      {/* More Information Button */}
      <div className="border-t pt-4">
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setIsMoreInfoOpen(true)}
        >
          <Info className="h-4 w-4 mr-2" />
          {t('form.moreInformation') || 'More Information'}
          {formData.custom_field_values?.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {formData.custom_field_values.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* More Information Panel */}
      <PatientMoreInfoPanel
        isOpen={isMoreInfoOpen}
        onClose={() => setIsMoreInfoOpen(false)}
        customFieldValues={formData.custom_field_values || []}
        onValuesChange={(values) => setFormData({ ...formData, custom_field_values: values })}
        t={t}
      />

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button 
          type="submit" 
          className="bg-[#E63946] hover:bg-[#C92A36]" 
          disabled={loading || !formData.address?.city_id}
          data-testid="save-patient-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          {patient ? t('form.update') : t('form.save')}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const PatientsPage = () => {
  const { t } = useTranslation('patients');
  const [patients, setPatients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  
  // Import/Export state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCountries();
    fetchCities();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
    
    fetchPatients(params);
  }, [search, statusFilter]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations/cities`);
      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchPatients = async (params = new URLSearchParams()) => {
    try {
      const response = await axios.get(`${API_URL}/api/patients?${params.toString()}`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      await axios.post(`${API_URL}/api/patients`, data);
      toast.success(t('messages.created'));
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorCreating'));
      throw error;
    }
  };

  const handleUpdate = async (data) => {
    try {
      await axios.put(`${API_URL}/api/patients/${selectedPatient.id}`, data);
      toast.success(t('messages.updated'));
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorUpdating'));
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    
    try {
      await axios.delete(`${API_URL}/api/patients/${id}`);
      toast.success(t('messages.deleted'));
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorDeleting'));
    }
  };

  const openEdit = (patient) => {
    setSelectedPatient(patient);
    setIsFormOpen(true);
  };

  const openView = async (patientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/patients/${patientId}`);
      setSelectedPatient(response.data);
      setIsViewOpen(true);
    } catch (error) {
      toast.error(t('messages.errorLoadingDetails'));
    }
  };

  // Import/Export handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients/export/template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'patients_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('importExport.templateDownloaded'));
    } catch (error) {
      toast.error(t('importExport.errorDownloadingTemplate'));
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/patients/export/data`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute('download', `patients_export_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('importExport.exportSuccess', { count: patients.length }));
    } catch (error) {
      toast.error(t('importExport.errorExporting'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error(t('importExport.onlyCsv'));
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setIsImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API_URL}/api/patients/import?skip_duplicates=true`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setImportResults(response.data);
      
      if (response.data.imported > 0) {
        toast.success(t('importExport.importSuccess', { count: response.data.imported }));
        fetchPatients();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('importExport.errorImporting'));
    } finally {
      setIsImporting(false);
    }
  };

  const closeImportDialog = () => {
    setIsImportOpen(false);
    setImportFile(null);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-2 space-y-6" data-testid="patients-page" style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--ink)]" style={{ letterSpacing: '-0.02em' }}>
            {t('title')}
          </h1>
          <p className="text-[var(--ink-3)] mt-1 text-sm">
            {t('subtitle')}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="list" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="list">
            <Users className="h-4 w-4" />
            {t('tabs.list') || 'Patients'}
          </TabsTrigger>
          <TabsTrigger value="groups">
            <FolderOpen className="h-4 w-4" />
            {t('tabs.groups') || 'Groups'}
          </TabsTrigger>
          <TabsTrigger value="customFields">
            <FileText className="h-4 w-4" />
            {t('tabs.customFields') || 'Custom Fields'}
          </TabsTrigger>
        </TabsList>

        {/* Patients List Tab */}
        <TabsContent value="list" className="space-y-4 mt-6">
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3">
            {/* Import/Export Buttons */}
            <Button 
              variant="outline"
              onClick={() => setIsImportOpen(true)}
              data-testid="import-patients-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {t('importExport.import')}
            </Button>
            <Button 
              variant="outline"
              onClick={handleExport}
              disabled={isExporting || patients.length === 0}
              data-testid="export-patients-btn"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              {t('importExport.export')}
            </Button>
            <Button 
              className="bg-[#E63946] hover:bg-[#C92A36]"
              onClick={() => { setSelectedPatient(null); setIsFormOpen(true); }}
              data-testid="add-patient-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newPatient')}
            </Button>
          </div>

          {/* Filters */}
          <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ink-3)]" />
                  <Input
                    placeholder={t('searchPlaceholder')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-[var(--bg-soft)] border-[var(--line)] focus:border-[var(--red)] focus:ring-[var(--red)]/10"
                    data-testid="patient-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 bg-[var(--bg-soft)] border-[var(--line)]" data-testid="patient-status-filter">
                    <Filter className="h-4 w-4 mr-2 text-[var(--ink-3)]" />
                    <SelectValue placeholder={t('common:status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('status.all')}</SelectItem>
                    <SelectItem value="active">{t('status.active')}</SelectItem>
                    <SelectItem value="inactive">{t('status.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

      {/* Patients Table */}
      <Card className="bg-white shadow-[var(--shadow-sm)] border-0 rounded-xl overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-3 border-[var(--line)] border-t-[var(--red)] rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full" data-testid="patients-table">
                <thead>
                  <tr className="bg-[var(--bg-soft)]">
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.patient')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.document')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.contact')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.city')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.status')}</th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.alerts')}</th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-[var(--ink-3)] uppercase tracking-wider">{t('table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-12 text-center text-[var(--ink-3)]">
                        <Users className="h-12 w-12 mx-auto mb-3 text-[var(--ink-4)]" />
                        {t('table.noPatients')}
                      </td>
                    </tr>
                  ) : (
                    patients.map((patient) => (
                      <tr key={patient.id} className="border-b border-[var(--line-soft)] hover:bg-[var(--bg-soft)] transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-[var(--ink)]">
                            {patient.first_name} {patient.last_name}
                          </div>
                          {patient.insurance_provider && (
                            <div className="text-xs text-[var(--ink-3)]">{patient.insurance_provider}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[var(--ink-2)]">
                          {patient.document_type} {patient.document_number}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {patient.phone}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {patient.city}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            patient.status === 'active' 
                              ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300'
                          }>
                            {patient.status === 'active' ? t('status.active') : t('status.inactive')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {patient.alerts_count > 0 && (
                            <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 dark:bg-yellow-900/50 dark:text-yellow-300">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {patient.alerts_count}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openView(patient.id)}
                              data-testid={`view-patient-${patient.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(patient)}
                              data-testid={`edit-patient-${patient.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(patient.id)}
                              data-testid={`delete-patient-${patient.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                {t('tabs.groups') || 'Field Groups'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PatientCustomFieldGroupsTab t={t} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="customFields" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('tabs.customFields') || 'Custom Fields'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PatientCustomFieldsTab t={t} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Patient Form Panel (Right Side Sheet) */}
      <PatientFormPanel
        patient={selectedPatient}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedPatient(null);
        }}
        onSubmit={selectedPatient ? handleUpdate : handleCreate}
        countries={countries}
        cities={cities}
      />

      {/* View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('detail.title')}</DialogTitle>
          </DialogHeader>
          {selectedPatient && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">{t('detail.fullName')}</Label>
                  <p className="font-medium text-foreground">{selectedPatient.first_name} {selectedPatient.last_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.document')}</Label>
                  <p className="font-medium text-foreground">{selectedPatient.document_type} {selectedPatient.document_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.birthDate')}</Label>
                  <p className="font-medium text-foreground">{selectedPatient.birth_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.gender')}</Label>
                  <p className="font-medium text-foreground capitalize">
                    {t(`genders.${selectedPatient.gender}`)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.phone')}</Label>
                  <p className="font-medium text-foreground">{selectedPatient.phone}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">{t('detail.bloodType')}</Label>
                  <p className="font-medium text-foreground">{selectedPatient.blood_type || t('detail.notSpecified')}</p>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('detail.address')}</Label>
                <p className="font-medium text-foreground">
                  {selectedPatient.address?.street}, {selectedPatient.address?.city}
                  {selectedPatient.address?.department && `, ${selectedPatient.address.department}`}
                  {selectedPatient.address?.country && ` - ${selectedPatient.address.country}`}
                </p>
              </div>
              {selectedPatient.alerts && selectedPatient.alerts.length > 0 && (
                <div>
                  <Label className="text-muted-foreground">{t('detail.alerts')}</Label>
                  <div className="space-y-2 mt-2">
                    {selectedPatient.alerts.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                          <span className="font-medium text-yellow-800 dark:text-yellow-300">{alert.type}</span>
                          <Badge className={
                            alert.severity === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                            alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                          }>
                            {alert.severity}
                          </Badge>
                        </div>
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">{alert.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={isImportOpen} onOpenChange={closeImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#E63946]" />
              {t('importExport.importTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('importExport.importDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Download Template Button */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                {t('importExport.templateHint')}
              </p>
              <Button 
                variant="outline" 
                onClick={handleDownloadTemplate}
                className="w-full"
                data-testid="download-template-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                {t('importExport.downloadTemplate')}
              </Button>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>{t('importExport.selectFile')}</Label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  importFile 
                    ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-[#E63946] hover:bg-gray-50 dark:hover:bg-gray-800'
                } cursor-pointer`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="import-file-input"
                />
                {importFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-green-800 dark:text-green-200">{importFile.name}</p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {(importFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {t('importExport.dropOrClick')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('importExport.csvOnly')}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <h4 className="font-medium text-foreground">{t('importExport.results')}</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-background rounded border">
                    <p className="text-2xl font-bold text-foreground">{importResults.total}</p>
                    <p className="text-xs text-muted-foreground">{t('importExport.total')}</p>
                  </div>
                  <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded border border-green-200 dark:border-green-800">
                    <p className="text-2xl font-bold text-green-600">{importResults.imported}</p>
                    <p className="text-xs text-green-600">{t('importExport.imported')}</p>
                  </div>
                  <div className="p-2 bg-yellow-50 dark:bg-yellow-900/30 rounded border border-yellow-200 dark:border-yellow-800">
                    <p className="text-2xl font-bold text-yellow-600">{importResults.skipped}</p>
                    <p className="text-xs text-yellow-600">{t('importExport.skipped')}</p>
                  </div>
                </div>
                
                {/* Progress bar */}
                {importResults.total > 0 && (
                  <div className="space-y-1">
                    <Progress 
                      value={(importResults.imported / importResults.total) * 100} 
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      {Math.round((importResults.imported / importResults.total) * 100)}% {t('importExport.successRate')}
                    </p>
                  </div>
                )}

                {/* Errors */}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-2 flex items-center gap-1">
                      <XCircle className="h-4 w-4" />
                      {t('importExport.errors')} ({importResults.errors.length})
                    </p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {importResults.errors.slice(0, 10).map((err, idx) => (
                        <div key={idx} className="text-xs p-2 bg-red-50 dark:bg-red-900/20 rounded text-red-700 dark:text-red-300">
                          <span className="font-medium">{t('importExport.row')} {err.row}:</span> {err.error}
                        </div>
                      ))}
                      {importResults.errors.length > 10 && (
                        <p className="text-xs text-red-500 text-center">
                          +{importResults.errors.length - 10} {t('importExport.moreErrors')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeImportDialog}>
              {importResults ? t('common:close') : t('common:cancel')}
            </Button>
            {!importResults && (
              <Button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="bg-[#E63946] hover:bg-[#C92A36]"
                data-testid="import-submit-btn"
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('importExport.importing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {t('importExport.importNow')}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientsPage;
